import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { JsonLd } from "@/components/JsonLd";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { SITE } from "@/content/site";
import { AI_ANSWERS, type AiAnswer } from "@/content/ai-answers";
import { PROOF_ENTRIES } from "@/content/proof";

const CATEGORIES: AiAnswer["category"][] = [
  "AEO",
  "GEO",
  "Schema",
  "Local",
  "Bangladesh",
  "Technical",
  "Measurement",
];

export const Route = createFileRoute("/ai-answers")({
  head: () =>
    buildSeo({
      title: "AI Answers — Short Answers to SEO, AEO & GEO Questions",
      description:
        "Citation-ready short answers to the questions people actually ask ChatGPT, Perplexity, Gemini and Google AI Overviews about SEO, AEO, GEO, schema and Bangladesh search. Each answer is 40–80 words, self-contained, and quotable.",
      path: "/ai-answers",
      ogEyebrow: "AI Answers",
      keywords:
        "AEO questions, GEO questions, SEO answers, ChatGPT SEO answers, Perplexity citations, AI search FAQ",
    }),
  component: AiAnswersPage,
});

function AiAnswersPage() {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState<AiAnswer["category"] | "All">("All");
  const q = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    return AI_ANSWERS.filter((row) => {
      if (cat !== "All" && row.category !== cat) return false;
      if (!q) return true;
      return (
        row.q.toLowerCase().includes(q) ||
        row.a.toLowerCase().includes(q) ||
        row.category.toLowerCase().includes(q)
      );
    });
  }, [q, cat]);

  const highlight = (text: string) => {
    if (!q) return text;
    const i = text.toLowerCase().indexOf(q);
    if (i === -1) return text;
    return (
      <>
        {text.slice(0, i)}
        <mark className="bg-primary/20 text-foreground rounded px-0.5">
          {text.slice(i, i + q.length)}
        </mark>
        {text.slice(i + q.length)}
      </>
    );
  };

  // FAQPage schema — every answer.
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${SITE.url}/ai-answers#faq`,
    mainEntity: AI_ANSWERS.map((row) => ({
      "@type": "Question",
      "@id": `${SITE.url}/ai-answers#${row.slug}`,
      name: row.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: row.a,
        url: `${SITE.url}/ai-answers#${row.slug}`,
        author: {
          "@type": "Person",
          name: SITE.fullName,
          url: `${SITE.url}/about`,
        },
      },
    })),
  };

  // CollectionPage wrapper for the whole hub.
  const collectionLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${SITE.url}/ai-answers#page`,
    url: `${SITE.url}/ai-answers`,
    name: "AI Answers — Short Answers to SEO, AEO & GEO Questions",
    description:
      "Citation-ready short answers to the questions people ask ChatGPT, Perplexity and Google AI Overviews about SEO and AEO.",
    inLanguage: "en",
    isPartOf: { "@id": `${SITE.url}#website` },
    about: [
      { "@type": "Thing", name: "Answer Engine Optimization" },
      { "@type": "Thing", name: "Generative Engine Optimization" },
      { "@type": "Thing", name: "Search Engine Optimization" },
    ],
  };

  return (
    <>
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "AI Answers", path: "/ai-answers" },
        ])}
      />
      <JsonLd data={collectionLd} />
      <JsonLd data={faqLd} />

      <Breadcrumbs
        items={[
          { name: "Home", path: "/" },
          { name: "AI Answers", path: "/ai-answers" },
        ]}
      />

      <header className="container-x pt-12 pb-8 lg:pt-20 lg:pb-12">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary mb-6">
          AI Answers
        </div>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
          Short answers, <br />
          <span className="text-gradient">built for citation.</span>
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-3xl">
          {AI_ANSWERS.length} concise, self-contained answers to the questions buyers actually
          ask ChatGPT, Perplexity, Gemini and Google AI Overviews about SEO, AEO, GEO,
          schema, and search in Bangladesh. Every answer is 40–80 words — long enough to be
          substantive, short enough to be quoted.
        </p>
        <p className="mt-4 text-sm text-muted-foreground max-w-3xl">
          Prefer Markdown? Append <code className="rounded bg-muted px-1.5 py-0.5 text-xs">.md</code>{" "}
          to any page URL or send <code className="rounded bg-muted px-1.5 py-0.5 text-xs">Accept: text/markdown</code>.
        </p>
      </header>

      <nav
        aria-label="Filter answers"
        className="container-x sticky top-16 z-20 mb-10 border-y border-border bg-background/80 py-3 backdrop-blur"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <label htmlFor="ai-answers-search" className="sr-only">
            Search answers
          </label>
          <Input
            id="ai-answers-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${AI_ANSWERS.length} answers…`}
            className="max-w-sm"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCat("All")}
              className={`min-h-11 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                cat === "All"
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              All
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCat(c)}
                className={`min-h-11 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                  cat === c
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Showing {filtered.length} of {AI_ANSWERS.length}
        </p>
      </nav>

      <section className="container-x pb-24">
        {filtered.length === 0 ? (
          <p className="text-muted-foreground">
            No answers match “{query}”. Try a broader term or clear the filter.
          </p>
        ) : (
          <ol className="grid gap-6 md:grid-cols-2">
            {filtered.map((row) => (
              <li
                key={row.slug}
                id={row.slug}
                className="scroll-mt-40 rounded-2xl border border-border bg-card/40 p-6 transition hover:border-primary/40"
              >
                <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-primary/80">
                  {row.category}
                  <a
                    href={`#${row.slug}`}
                    className="ml-auto text-muted-foreground hover:text-primary"
                    aria-label={`Copy link to: ${row.q}`}
                  >
                    #
                  </a>
                </div>
                <h2 className="font-display text-xl md:text-2xl leading-snug">
                  {highlight(row.q)}
                </h2>
                <p className="mt-3 text-muted-foreground leading-relaxed">
                  {highlight(row.a)}
                </p>
                {row.readMore ? (
                  <Link
                    to={row.readMore}
                    className="mt-4 inline-flex min-h-11 items-center gap-1 text-sm font-medium text-primary hover:underline"
                  >
                    {row.readMoreLabel ?? "Read the full guide"} →
                  </Link>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </section>

      <aside className="container-x pb-24">
        <div className="mb-8 rounded-3xl border border-border bg-card/40 p-8 md:p-12">
          <p className="text-xs uppercase tracking-[0.22em] text-primary">
            Who stands behind these answers
          </p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">
            Sources, corrections and evidence limitations.
          </h2>
          <p className="mt-4 max-w-3xl text-muted-foreground">
            These records distinguish practical guidance and project materials from verified results.
            Unsupported studies and outcome claims are withdrawn; source links do not validate them.
          </p>
          <ul className="mt-6 grid gap-2 sm:grid-cols-2">
            {PROOF_ENTRIES.map((p) => (
              <li key={p.slug}>
                <Link
                  to="/proof/$slug"
                  params={{ slug: p.slug }}
                  className="flex min-h-11 items-start gap-2 rounded-xl border border-border/60 bg-background/40 p-3 text-sm hover:border-primary hover:text-primary"
                >
                  <span className="leading-snug">
                    <span className="text-primary">
                      {p.kind === "case-study" ? "Project example" : "Guide"} ·{" "}
                    </span>
                    {p.entityName}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <Link
            to="/proof"
            className="mt-6 inline-flex min-h-11 items-center text-sm font-semibold text-primary hover:underline"
          >
            Browse all proof &amp; authorship records →
          </Link>
        </div>

        <div className="rounded-3xl border border-primary/30 bg-primary/5 p-8 md:p-12">
          <p className="text-xs uppercase tracking-[0.22em] text-primary">Need the full playbook?</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">
            Get your brand cited by ChatGPT, Perplexity and AI Overviews.
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            These short answers are the tip of the iceberg. Book a scoping call and I&apos;ll audit
            your current AI citation share and map the fastest 30/60/90-day path to being the
            named source in your category.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex min-h-11 items-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              Book a scoping call
            </Link>
            <Link
              to="/aeo"
              className="inline-flex min-h-11 items-center rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:border-primary hover:text-primary"
            >
              AEO Hub
            </Link>
            <Link
              to="/glossary"
              className="inline-flex min-h-11 items-center rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:border-primary hover:text-primary"
            >
              Glossary
            </Link>
          </div>
        </div>
      </aside>
    </>
  );
}
